/**
 * Web Audio API によるプロシージャル音生成の部品。
 * 外部音源ファイルは一切使わない。
 */

export interface ToneSpec {
  /** 開始周波数 (Hz) */
  freq: number;
  /** 終了周波数。指定するとチャープになる */
  endFreq?: number;
  type?: OscillatorType;
  /** 立ち上がり (秒) */
  attack?: number;
  /** 減衰 (秒) */
  decay?: number;
  /** 音量 0..1 */
  gain?: number;
  /** 開始を遅らせる (秒) */
  delay?: number;
  /** ローパスのカットオフ (Hz)。省略時はフィルタ無し */
  lowpass?: number;
}

export interface NoiseSpec {
  /** 長さ (秒) */
  duration: number;
  /** バンドパス中心周波数 (Hz) */
  center: number;
  /** Q 値。大きいほど鋭い */
  q?: number;
  gain?: number;
  delay?: number;
  /** 'lowpass' で「こもった」音になる */
  filter?: BiquadFilterType;
}

/** ホワイトノイズのバッファを 1 度だけ作って使い回す */
export function createNoiseBuffer(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/** 単発のトーン（サイン波・矩形波など）を鳴らす */
export function playTone(ctx: AudioContext, destination: AudioNode, spec: ToneSpec): void {
  const now = ctx.currentTime + (spec.delay ?? 0);
  const attack = spec.attack ?? 0.005;
  const decay = spec.decay ?? 0.12;
  const gainValue = spec.gain ?? 0.3;

  const osc = ctx.createOscillator();
  osc.type = spec.type ?? 'sine';
  osc.frequency.setValueAtTime(spec.freq, now);
  if (spec.endFreq !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, spec.endFreq), now + decay);
  }

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), now + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay);

  let node: AudioNode = osc;
  if (spec.lowpass) {
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(spec.lowpass, now);
    osc.connect(filter);
    node = filter;
  }
  node.connect(gain);
  gain.connect(destination);

  osc.start(now);
  osc.stop(now + attack + decay + 0.05);
}

/** ノイズバーストを鳴らす（足音・カチッ音の素） */
export function playNoise(
  ctx: AudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  spec: NoiseSpec,
): void {
  const now = ctx.currentTime + (spec.delay ?? 0);
  const gainValue = spec.gain ?? 0.3;

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  // 毎回違う位置から再生して、同じ音の繰り返しに聞こえないようにする
  const offset = Math.random() * Math.max(0.001, buffer.duration - spec.duration - 0.01);

  const filter = ctx.createBiquadFilter();
  filter.type = spec.filter ?? 'bandpass';
  filter.frequency.setValueAtTime(spec.center, now);
  filter.Q.setValueAtTime(spec.q ?? 1, now);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), now + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + spec.duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(destination);

  source.start(now, offset);
  source.stop(now + spec.duration + 0.05);
}

/** アルペジオ（章クリアのジングルや通知音） */
export function playArpeggio(
  ctx: AudioContext,
  destination: AudioNode,
  freqs: number[],
  options: { step?: number; type?: OscillatorType; gain?: number; decay?: number } = {},
): void {
  const step = options.step ?? 0.09;
  freqs.forEach((freq, index) => {
    playTone(ctx, destination, {
      freq,
      type: options.type ?? 'triangle',
      gain: options.gain ?? 0.22,
      decay: options.decay ?? 0.28,
      delay: index * step,
      attack: 0.008,
    });
  });
}
